import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { WidgetCard } from '../components/WidgetCard';
import { Clock, Users, Folder, Camera } from 'lucide-react';
import WeatherForecast from '../components/WeatherForecast';
import { AddToHomeScreen } from '../components/AddToHomeScreen';
import { PendingUsersAlert } from '../components/PendingUsersAlert';
import { DailyVideo } from '../components/DailyVideo';
import { UpcomingEvents } from '../components/UpcomingEvents';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    projects: 0,
    teamMembers: 0,
    photos: 0,
    hoursLogged: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [clockStatus, setClockStatus] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, usersRes, clockRes, statsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`),
        axios.get(`${API}/timelog/status`),
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: { total_photos: 0, total_hours: 0 } }))
      ]);

      const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      const dashboardStats = statsRes.data;

      setStats({
        projects: projects.length,
        teamMembers: users.length,
        photos: dashboardStats.total_photos,
        hoursLogged: Math.round(dashboardStats.total_hours)
      });

      setRecentProjects(projects.slice(0, 5));
      setClockStatus(clockRes.data);
      
      // Filter online users
      const online = users.filter(u => u.is_online);
      setOnlineUsers(online);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setRecentProjects([]);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm mb-1 font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Welcome back, {user.name}!</h1>
          <p className="text-gray-600 text-base">Here's what's happening with your projects today.</p>
        </div>

        <WidgetCard />

        {clockStatus?.clocked_in && (
          <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-6" data-testid="clock-status">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-900 font-semibold">Currently Clocked In</span>
              </div>
              <span className="text-green-700 text-sm font-medium">Since {new Date(clockStatus.clock_in_time).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}</span>
            </div>
          </div>
        )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Folder}
          label="Active Projects"
          value={stats.projects}
          color="bg-red-100 text-red-600"
        />
        <StatCard
          icon={Users}
          label="Team Members"
          value={stats.teamMembers}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          icon={Camera}
          label="Project Photos"
          value={stats.photos}
          color="bg-purple-100 text-purple-600"
        />
        <StatCard
          icon={Clock}
          label="Hours Logged"
          value={stats.hoursLogged}
          color="bg-green-100 text-green-600"
        />
      </div>

      {/* Upcoming Events Alert */}
      <div className="mb-6">
        <UpcomingEvents limit={5} userRole={user.role} />
      </div>

      {/* Pending Users Alert */}
      <div className="mb-6">
        <PendingUsersAlert user={user} />
      </div>

      {/* Add to Home Screen Prompt */}
      <div className="mb-6">
        <AddToHomeScreen />
      </div>

      {/* Daily Horoscope */}
      <div className="mb-8">
        <WeatherForecast />
      </div>

      {/* Daily Video */}
      <div className="mb-8">
        <DailyVideo />
      </div>

      {/* Online Users - Only Walter (admin@dawalconnect.ca) */}
      {user?.email === 'admin@dawalconnect.ca' && onlineUsers.length > 0 && (
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            Online Now ({onlineUsers.length})
          </h2>
          <p className="text-sm text-gray-600 mb-4">Team members, clients, and sub-contractors currently active</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {onlineUsers.map((member) => {
              const getRoleColor = (role) => {
                switch(role) {
                  case 'admin': return 'bg-purple-500';
                  case 'employee': return 'bg-blue-500';
                  case 'sub_contractor': return 'bg-yellow-500';
                  case 'client': return 'bg-green-500';
                  default: return 'bg-gray-500';
                }
              };
              
              const getRoleBadge = (role) => {
                switch(role) {
                  case 'admin': return 'bg-purple-100 text-purple-700';
                  case 'employee': return 'bg-blue-100 text-blue-700';
                  case 'sub_contractor': return 'bg-yellow-100 text-yellow-700';
                  case 'client': return 'bg-green-100 text-green-700';
                  default: return 'bg-gray-100 text-gray-700';
                }
              };

              return (
                <div
                  key={member.id}
                  className="p-3 bg-white border-2 border-green-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className={`w-10 h-10 ${getRoleColor(member.role)} text-white rounded-full flex items-center justify-center font-bold`}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full animate-pulse"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{member.name}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getRoleBadge(member.role)}`}>
                        {member.role.replace('_', ' ').toUpperCase()}
                      </span>
                      {member.current_page && (
                        <p className="text-xs text-gray-600 truncate mt-1">📍 {member.current_page}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Projects</h2>
        {recentProjects.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No projects yet</p>
        ) : (
          <div className="space-y-3">
            {recentProjects.map((project) => (
              <div
                key={project.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                data-testid={`project-${project.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-gray-900 font-semibold text-lg">{project.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{project.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    project.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                    project.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Dashboard;